import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { BarChart3, TrendingUp, Mail, Phone, RefreshCw, CheckCircle, AlertCircle, Settings } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { CHART_COLORS, DOUGHNUT_SERIES, applyChartDefaults } from '../utils/chartColors';
import './AnalyticsPage.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);
applyChartDefaults(ChartJS);

function AnalyticsPage() {
  const [dashboardData, setDashboardData] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState({ emails: [], sms: [] });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [providerStats, setProviderStats] = useState(null);
  const [providerStatsDays, setProviderStatsDays] = useState(30);
  const [providerStatsLoading, setProviderStatsLoading] = useState(false);
  const [campaignStats, setCampaignStats] = useState([]);
  const [campaignStatsLoading, setCampaignStatsLoading] = useState(false);

  useEffect(() => {
    loadAnalyticsData();
  }, [period]);

  useEffect(() => {
    loadProviderStats();
  }, [providerStatsDays]);

  useEffect(() => {
    loadCampaignStats();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);

      const [dashboard, emailTimeSeries, smsTimeSeries] = await Promise.all([
        analyticsAPI.getDashboard({ period: 'this_year' }),
        analyticsAPI.getTimeSeries('emails_sent', { period }),
        analyticsAPI.getTimeSeries('sms_sent', { period }),
      ]);

      setDashboardData(dashboard.data);
      setTimeSeriesData({
        emails: emailTimeSeries.data?.data || [],
        sms: smsTimeSeries.data?.data || []
      });
    } catch (error) {
      console.error('Error loading analytics data:', error?.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProviderStats = async () => {
    try {
      setProviderStatsLoading(true);
      const resp = await analyticsAPI.getProviderStats(providerStatsDays);
      setProviderStats(resp.data);
    } catch (error) {
      console.error('Error loading provider stats:', error);
    } finally {
      setProviderStatsLoading(false);
    }
  };

  const loadCampaignStats = async () => {
    try {
      setCampaignStatsLoading(true);
      const resp = await analyticsAPI.getCampaignStats();
      setCampaignStats(resp.data?.campaigns || []);
    } catch (error) {
      console.error('Error loading campaign stats:', error?.response?.data || error.message);
    } finally {
      setCampaignStatsLoading(false);
    }
  };

  // Chart data formatters
  const getTimeSeriesChartData = () => {
    const labels = timeSeriesData.emails.map(d => d.date) || [];

    return {
      labels,
      datasets: [
        {
          label: 'Emails Sent',
          data: timeSeriesData.emails.map(d => d.value),
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_COLORS.primaryFill,
          tension: 0.3,
          fill: true,
        },
        {
          label: 'SMS Sent',
          data: timeSeriesData.sms.map(d => d.value),
          borderColor: CHART_COLORS.secondary,
          backgroundColor: CHART_COLORS.secondaryFill,
          tension: 0.3,
          fill: true,
        }
      ]
    };
  };

  const getSendGridDailyChartData = () => {
    const daily = providerStats?.providers?.sendgrid?.daily || [];
    return {
      labels: daily.map(d => d.date),
      datasets: [
        {
          label: 'Delivered',
          data: daily.map(d => d.delivered),
          borderColor: CHART_COLORS.success,
          backgroundColor: CHART_COLORS.successFill,
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Opens',
          data: daily.map(d => d.opens),
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_COLORS.primaryFill,
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Clicks',
          data: daily.map(d => d.clicks),
          borderColor: CHART_COLORS.accent,
          backgroundColor: CHART_COLORS.accentFill,
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Bounces',
          data: daily.map(d => d.bounces),
          borderColor: CHART_COLORS.danger,
          backgroundColor: CHART_COLORS.dangerFill,
          tension: 0.3,
          fill: false,
        },
      ],
    };
  };

  const getResendStatusChartData = () => {
    const breakdown = providerStats?.providers?.resend?.status_breakdown || {};
    const colorMap = {
      sent: DOUGHNUT_SERIES[0],
      delivered: CHART_COLORS.success,
      opened: DOUGHNUT_SERIES[1],
      clicked: DOUGHNUT_SERIES[2],
      bounced: CHART_COLORS.danger,
      complained: CHART_COLORS.warn,
      delivery_delayed: CHART_COLORS.neutral,
    };
    const labels = Object.keys(breakdown);
    return {
      labels: labels.map(l => l.charAt(0).toUpperCase() + l.slice(1).replace('_', ' ')),
      datasets: [{
        data: Object.values(breakdown),
        backgroundColor: labels.map(l => colorMap[l] || DOUGHNUT_SERIES[3]),
        borderWidth: 2,
        borderColor: CHART_COLORS.surface,
      }],
    };
  };

  const getResendDailyChartData = () => {
    const daily = providerStats?.providers?.resend?.daily || [];
    return {
      labels: daily.map(d => d.date),
      datasets: [
        {
          label: 'Sent',
          data: daily.map(d => d.sent),
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_COLORS.primaryFill,
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Delivered',
          data: daily.map(d => d.delivered),
          borderColor: CHART_COLORS.success,
          backgroundColor: CHART_COLORS.successFill,
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Clicked',
          data: daily.map(d => d.clicked),
          borderColor: CHART_COLORS.accent,
          backgroundColor: CHART_COLORS.accentFill,
          tension: 0.3,
          fill: false,
        },
        {
          label: 'Bounced',
          data: daily.map(d => d.bounced),
          borderColor: CHART_COLORS.danger,
          backgroundColor: CHART_COLORS.dangerFill,
          tension: 0.3,
          fill: false,
        },
      ],
    };
  };

  const getTwilioStatusChartData = () => {
    const breakdown = providerStats?.providers?.twilio?.status_breakdown || {};
    const colorMap = {
      delivered: CHART_COLORS.success,
      sent: DOUGHNUT_SERIES[0],
      queued: CHART_COLORS.neutral,
      failed: CHART_COLORS.danger,
      undelivered: CHART_COLORS.warn,
    };
    const labels = Object.keys(breakdown);
    return {
      labels: labels.map(l => l.charAt(0).toUpperCase() + l.slice(1)),
      datasets: [{
        data: Object.values(breakdown),
        backgroundColor: labels.map(l => colorMap[l] || DOUGHNUT_SERIES[3]),
        borderWidth: 2,
        borderColor: CHART_COLORS.surface,
      }],
    };
  };

  const fmtNum = (n) => {
    if (n === undefined || n === null) return '—';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  const fmtRate = (r) => (r !== undefined && r !== null) ? `${r}%` : '—';

  const getEmailsSent = () => {
    const p = providerStats?.providers;
    if (!p) return null;
    let total = 0;
    const sources = [];
    if (p.resend?.total !== undefined && !p.resend.error && p.resend.configured !== false) {
      total += p.resend.total;
      sources.push('Resend');
    }
    if (p.sendgrid?.totals?.requests !== undefined && !p.sendgrid.error && p.sendgrid.configured !== false) {
      total += p.sendgrid.totals.requests;
      sources.push('SendGrid');
    }
    if (sources.length === 0) return null;
    return { count: total, source: sources.join(' + ') };
  };

  const getSMSSent = () => {
    const p = providerStats?.providers;
    if (!p) return null;
    if (p.twilio?.total_sent !== undefined && !p.twilio.error && p.twilio.configured !== false)
      return { count: p.twilio.total_sent, source: 'Twilio' };
    return null;
  };

  const getBestDeliveryRate = () => {
    const p = providerStats?.providers;
    if (!p) return null;
    if (p.resend?.rates?.delivery_rate !== undefined && !p.resend.error && p.resend.configured !== false)
      return { rate: p.resend.rates.delivery_rate, source: 'Resend' };
    if (p.sendgrid?.rates?.delivery_rate !== undefined && !p.sendgrid.error && p.sendgrid.configured !== false)
      return { rate: p.sendgrid.rates.delivery_rate, source: 'SendGrid' };
    return null;
  };

  const getFallbackDeliveryRate = () => {
    if (dashboardData?.delivery_rate === undefined || dashboardData?.delivery_rate === null) return null;
    return { rate: dashboardData.delivery_rate, source: 'Dashboard' };
  };

  const getActivityChartData = () => {
    const resendDaily = providerStats?.providers?.resend?.daily;
    if (resendDaily && resendDaily.length > 0)
      return { data: getResendDailyChartData(), source: 'Resend' };
    const sgDaily = providerStats?.providers?.sendgrid?.daily;
    if (sgDaily && sgDaily.length > 0)
      return { data: getSendGridDailyChartData(), source: 'SendGrid' };
    if (timeSeriesData.emails.length > 0 || timeSeriesData.sms.length > 0)
      return { data: getTimeSeriesChartData(), source: null };
    return null;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h1 className="page-title">
          <BarChart3 className="title-icon" />
          Analytics Dashboard
        </h1>
        <p className="page-description">
          Track your outreach to prospects — the businesses you're selling leads to
        </p>
      </div>

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-header">
            <Mail className="metric-icon" />
            <span className="metric-label">Emails Sent</span>
          </div>
          <div className="metric-value">
            {campaignStatsLoading ? '…' : fmtNum(campaignStats.reduce((s, c) => s + (Number(c.emails_sent) || 0), 0))}
          </div>
          <div className="metric-sublabel">all campaigns · this app</div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <Phone className="metric-icon" />
            <span className="metric-label">SMS Sent</span>
          </div>
          <div className="metric-value">
            {campaignStatsLoading ? '…' : fmtNum(campaignStats.reduce((s, c) => s + (Number(c.sms_sent) || 0), 0))}
          </div>
          <div className="metric-sublabel">all campaigns · this app</div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <CheckCircle className="metric-icon" />
            <span className="metric-label">Delivery Rate</span>
          </div>
          <div className="metric-value">
            {providerStatsLoading
              ? '…'
              : getBestDeliveryRate()
                ? `${getBestDeliveryRate().rate}%`
                : getFallbackDeliveryRate()
                  ? `${getFallbackDeliveryRate().rate}%`
                  : '—'}
          </div>
          <div className="metric-sublabel">
            {getBestDeliveryRate()
              ? `via ${getBestDeliveryRate().source} · last ${providerStatsDays} days`
              : getFallbackDeliveryRate()
                ? 'last 30 days'
                : 'connect a provider'}
          </div>
        </div>

      </div>

      {/* ─── Live Provider Analytics ───────────────────────────────────── */}
      <div className="provider-analytics-section">
        <div className="provider-section-header">
          <div>
            <h2 className="provider-section-title">Live Provider Analytics</h2>
            <p className="provider-section-desc">
              Real-time data pulled directly from your connected provider accounts
              {providerStats && (
                <span className="provider-date-range">
                  {' '}· {providerStats.start_date} → {providerStats.end_date}
                </span>
              )}
            </p>
          </div>
          <div className="provider-controls">
            <div className="period-selector">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  className={`period-btn${providerStatsDays === d ? ' active' : ''}`}
                  onClick={() => setProviderStatsDays(d)}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              className="refresh-btn"
              onClick={loadProviderStats}
              disabled={providerStatsLoading}
            >
              <RefreshCw size={14} className={providerStatsLoading ? 'spinning' : ''} />
              {providerStatsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {providerStatsLoading ? (
          <div className="provider-loading">
            <div className="spinner" />
            <p>Loading provider analytics…</p>
          </div>
        ) : (
          <div className="providers-grid">

            {/* ── Resend Card ── */}
            {(() => {
              const resend = providerStats?.providers?.resend;
              return (
                <div className="provider-card">
                  <div className="provider-card-header">
                    <div className="provider-badge resend-badge">
                      <Mail size={16} />
                      Resend
                    </div>
                    {resend?.configured === false ? (
                      <span className="config-status unconfigured">Not configured</span>
                    ) : resend?.error ? (
                      <span className="config-status error"><AlertCircle size={12} /> Error</span>
                    ) : resend ? (
                      <span className="config-status ok"><CheckCircle size={12} /> Live</span>
                    ) : null}
                  </div>

                  {!resend || resend?.configured === false ? (
                    <div className="provider-empty">
                      <Mail size={40} className="provider-empty-icon" />
                      <p>Add your Resend API key in <a href="/settings">Settings</a> to unlock email delivery, open rate, click rate, and daily trend data.</p>
                      <a href="/settings" className="provider-setup-link">
                        <Settings size={14} /> Configure Resend
                      </a>
                    </div>
                  ) : resend?.error ? (
                    <div className="provider-error">
                      <AlertCircle size={32} />
                      <p>{resend.error}</p>
                    </div>
                  ) : resend ? (
                    <>
                      {/* Debug info — shows exactly what Resend returned */}
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--surface-subtle)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '6px 10px', marginBottom: '10px', lineHeight: '1.8' }}>
                        <strong>Resend API data source:</strong><br/>
                        Fetched from API: <strong>{resend.fetched_total ?? '—'}</strong> emails (most recent, account-wide)<br/>
                        Matched last {providerStatsDays} days: <strong>{resend.total}</strong> emails<br/>
                        {resend.fetched_total === 1000 && (
                          <span style={{ color: 'var(--danger)' }}>⚠ Hit the 1,000 fetch cap — emails older than the most recent 1,000 are not shown<br/></span>
                        )}
                        {(() => {
                          const activeDays = (resend.daily || []).filter(d => d.sent > 0);
                          if (activeDays.length === 0) return null;
                          return (
                            <>
                              <strong>Emails by day:</strong><br/>
                              {activeDays.map(d => (
                                <span key={d.date}>
                                  {d.date}: <strong>{d.sent}</strong> sent, {d.delivered} delivered, {d.bounced} bounced<br/>
                                </span>
                              ))}
                              {activeDays.length === 1 && (
                                <span style={{ color: 'var(--warn)' }}>⚠ All emails on one day — batch campaign sent on {activeDays[0].date}</span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div className="provider-metrics-grid">
                        <div className="provider-metric">
                          <span className="pm-value">{fmtNum(resend.total)}</span>
                          <span className="pm-label">In Period</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value green">{fmtNum(resend.delivered)}</span>
                          <span className="pm-label">Delivered</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value amber">{fmtNum(resend.clicked)}</span>
                          <span className="pm-label">Clicked</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value red">{fmtNum(resend.bounced)}</span>
                          <span className="pm-label">Bounced</span>
                        </div>
                      </div>
                      <div className="provider-rates">
                        <div className="rate-item">
                          <span className="rate-label">Delivery</span>
                          <span className="rate-value green">{fmtRate(resend.rates?.delivery_rate)}</span>
                        </div>
                        <div className="rate-item">
                          <span className="rate-label">Click</span>
                          <span className="rate-value amber">{fmtRate(resend.rates?.click_rate)}</span>
                        </div>
                        <div className="rate-item">
                          <span className="rate-label">Bounce</span>
                          <span className="rate-value red">{fmtRate(resend.rates?.bounce_rate)}</span>
                        </div>
                      </div>
                      {resend.daily && resend.daily.length > 0 ? (
                        <div className="provider-chart-wrapper">
                          <p className="chart-sub-label">Daily trend</p>
                          <Line
                            data={getResendDailyChartData()}
                            options={{
                              ...chartOptions,
                              plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
                            }}
                          />
                        </div>
                      ) : resend.status_breakdown && Object.keys(resend.status_breakdown).length > 0 ? (
                        <div className="provider-chart-wrapper donut-chart">
                          <p className="chart-sub-label">Status breakdown</p>
                          <Doughnut
                            data={getResendStatusChartData()}
                            options={{
                              ...chartOptions,
                              plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
                            }}
                          />
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              );
            })()}

            {/* ── Twilio Card ── */}
            {(() => {
              const twilio = providerStats?.providers?.twilio;
              return (
                <div className="provider-card">
                  <div className="provider-card-header">
                    <div className="provider-badge twilio-badge">
                      <Phone size={16} />
                      Twilio
                    </div>
                    {twilio?.configured === false ? (
                      <span className="config-status unconfigured">Not configured</span>
                    ) : twilio?.error ? (
                      <span className="config-status error"><AlertCircle size={12} /> Error</span>
                    ) : twilio ? (
                      <span className="config-status ok"><CheckCircle size={12} /> Live</span>
                    ) : null}
                  </div>

                  {!twilio || twilio?.configured === false ? (
                    <div className="provider-empty">
                      <Phone size={40} className="provider-empty-icon" />
                      <p>Add your Twilio credentials in <a href="/settings">Settings</a> to unlock SMS delivery stats, cost tracking, and status breakdown.</p>
                      <a href="/settings" className="provider-setup-link">
                        <Settings size={14} /> Configure Twilio
                      </a>
                    </div>
                  ) : twilio?.error ? (
                    <div className="provider-error">
                      <AlertCircle size={32} />
                      <p>{twilio.error}</p>
                    </div>
                  ) : twilio ? (
                    <>
                      <div className="provider-metrics-grid">
                        <div className="provider-metric">
                          <span className="pm-value">{fmtNum(twilio.total_sent)}</span>
                          <span className="pm-label">Total Sent</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value green">{fmtNum(twilio.delivered)}</span>
                          <span className="pm-label">Delivered</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value red">{fmtNum(twilio.failed)}</span>
                          <span className="pm-label">Failed</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value amber">
                            {twilio.total_cost > 0
                              ? `${twilio.currency || 'USD'} ${twilio.total_cost?.toFixed(2)}`
                              : '—'}
                          </span>
                          <span className="pm-label">Cost</span>
                        </div>
                      </div>
                      <div className="provider-rates">
                        <div className="rate-item">
                          <span className="rate-label">Delivery</span>
                          <span className="rate-value green">{fmtRate(twilio.delivery_rate)}</span>
                        </div>
                      </div>
                      {twilio.status_breakdown && Object.keys(twilio.status_breakdown).length > 0 && (
                        <div className="provider-chart-wrapper donut-chart">
                          <p className="chart-sub-label">Status breakdown</p>
                          <Doughnut
                            data={getTwilioStatusChartData()}
                            options={{
                              ...chartOptions,
                              plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
                            }}
                          />
                        </div>
                      )}
                      {twilio.note && (
                        <p className="provider-note">{twilio.note}</p>
                      )}
                    </>
                  ) : null}
                </div>
              );
            })()}

            {/* ── SendGrid Card ── (hidden — not in use, re-enable when needed) */}
            {false && (() => {
              const sg = providerStats?.providers?.sendgrid;
              return (
                <div className="provider-card">
                  <div className="provider-card-header">
                    <div className="provider-badge sendgrid-badge">
                      <Mail size={16} />
                      SendGrid
                    </div>
                    {sg?.configured === false ? (
                      <span className="config-status unconfigured">Not configured</span>
                    ) : sg?.error ? (
                      <span className="config-status error"><AlertCircle size={12} /> Error</span>
                    ) : sg ? (
                      <span className="config-status ok"><CheckCircle size={12} /> Live</span>
                    ) : null}
                  </div>

                  {!sg || sg?.configured === false ? (
                    <div className="provider-empty">
                      <Mail size={40} className="provider-empty-icon" />
                      <p>Add your SendGrid API key in <a href="/settings">Settings</a> to unlock email delivery, open rate, click rate, bounce tracking, and spam report data.</p>
                      <a href="/settings" className="provider-setup-link">
                        <Settings size={14} /> Configure SendGrid
                      </a>
                    </div>
                  ) : sg?.error ? (
                    <div className="provider-error">
                      <AlertCircle size={32} />
                      <p>{sg.error}</p>
                    </div>
                  ) : sg ? (
                    <>
                      <div className="provider-metrics-grid">
                        <div className="provider-metric">
                          <span className="pm-value">{fmtNum(sg.totals?.requests)}</span>
                          <span className="pm-label">Requests</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value green">{fmtNum(sg.totals?.delivered)}</span>
                          <span className="pm-label">Delivered</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value indigo">{fmtNum(sg.totals?.unique_opens)}</span>
                          <span className="pm-label">Unique Opens</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value amber">{fmtNum(sg.totals?.unique_clicks)}</span>
                          <span className="pm-label">Unique Clicks</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value red">{fmtNum(sg.totals?.bounces)}</span>
                          <span className="pm-label">Bounces</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value orange">{fmtNum(sg.totals?.spam_reports)}</span>
                          <span className="pm-label">Spam Reports</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value slate">{fmtNum(sg.totals?.unsubscribes)}</span>
                          <span className="pm-label">Unsubscribes</span>
                        </div>
                        <div className="provider-metric">
                          <span className="pm-value slate">{fmtNum(sg.totals?.blocks)}</span>
                          <span className="pm-label">Blocks</span>
                        </div>
                      </div>
                      <div className="provider-rates">
                        <div className="rate-item">
                          <span className="rate-label">Delivery</span>
                          <span className="rate-value green">{fmtRate(sg.rates?.delivery_rate)}</span>
                        </div>
                        <div className="rate-item">
                          <span className="rate-label">Open</span>
                          <span className="rate-value indigo">{fmtRate(sg.rates?.open_rate)}</span>
                        </div>
                        <div className="rate-item">
                          <span className="rate-label">Click</span>
                          <span className="rate-value amber">{fmtRate(sg.rates?.click_rate)}</span>
                        </div>
                        <div className="rate-item">
                          <span className="rate-label">Bounce</span>
                          <span className="rate-value red">{fmtRate(sg.rates?.bounce_rate)}</span>
                        </div>
                        <div className="rate-item">
                          <span className="rate-label">Spam</span>
                          <span className="rate-value orange">{fmtRate(sg.rates?.spam_rate)}</span>
                        </div>
                        <div className="rate-item">
                          <span className="rate-label">Unsub</span>
                          <span className="rate-value slate">{fmtRate(sg.rates?.unsubscribe_rate)}</span>
                        </div>
                      </div>
                      {sg.daily && sg.daily.length > 0 && (
                        <div className="provider-chart-wrapper">
                          <p className="chart-sub-label">Daily trend — Delivered / Opens / Clicks / Bounces</p>
                          <Line
                            data={getSendGridDailyChartData()}
                            options={{
                              ...chartOptions,
                              plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
                            }}
                          />
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              );
            })()} {/* end SendGrid hidden block */}

          </div>
        )}
      </div>

      {/* ─── Sending Activity ───────────────────────────────────────────── */}
      <div className="chart-container full-width" style={{ marginTop: '1.5rem' }}>
        {(() => {
          const activity = getActivityChartData();
          const title = activity?.source
            ? `Sending Activity — Last ${providerStatsDays} days via ${activity.source}`
            : `Sending Activity Over Time (Last ${period})`;
          return (
            <>
              <h3>{title}</h3>
              <div className="chart-wrapper" style={{ height: '300px' }}>
                {activity ? (
                  <Line data={activity.data} options={chartOptions} />
                ) : (
                  <div className="chart-placeholder">
                    <TrendingUp className="chart-icon" />
                    <p>No sending activity yet — connect a provider or start a campaign to see data here</p>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* ─── Campaign Breakdown ─────────────────────────────────────────── */}
      <div className="campaign-analytics-section" style={{ marginTop: '2rem' }}>
        <div className="provider-section-header">
          <div>
            <h2 className="provider-section-title">Campaign Performance</h2>
            <p className="provider-section-desc">
              Stats broken down by individual campaign
              <span className="provider-date-range"> · all-time totals, not affected by the date range above</span>
            </p>
          </div>
          <button
            className="refresh-btn"
            onClick={loadCampaignStats}
            disabled={campaignStatsLoading}
          >
            <RefreshCw size={14} className={campaignStatsLoading ? 'spinning' : ''} />
            {campaignStatsLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {campaignStatsLoading ? (
          <div className="provider-loading">
            <div className="spinner" />
            <p>Loading campaign stats…</p>
          </div>
        ) : campaignStats.length === 0 ? (
          <div className="chart-placeholder" style={{ padding: '2rem' }}>
            <BarChart3 className="chart-icon" />
            <p>No campaigns yet — launch a campaign to see per-campaign analytics here</p>
          </div>
        ) : (
          <div className="campaign-table-wrapper">
            <table className="campaign-analytics-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Clicked</th>
                  <th>Bounced</th>
                  <th>Failed</th>
                  <th>Delivery %</th>
                  <th>Click %</th>
                  <th>Bounce %</th>
                </tr>
              </thead>
              <tbody>
                {campaignStats.map(c => (
                  <tr key={c.id}>
                    <td className="campaign-name-cell">
                      <span className="campaign-name">{c.name || `Campaign #${c.id}`}</span>
                      {c.launched_at && (
                        <span className="campaign-date">
                          {new Date(c.launched_at).toLocaleDateString()}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`campaign-status-badge status-${c.status}`}>
                        {c.status}
                      </span>
                    </td>
                    <td>{fmtNum(c.total_sent)}</td>
                    <td className="metric-green">{fmtNum(c.delivered)}</td>
                    <td className={c.clicked > 0 ? 'metric-amber' : ''}>{fmtNum(c.clicked)}</td>
                    <td className={c.bounced > 0 ? 'metric-red' : ''}>{fmtNum(c.bounced)}</td>
                    <td className={c.failed > 0 ? 'metric-red' : ''}>{fmtNum(c.failed)}</td>
                    <td className="metric-green">{c.total_sent > 0 ? `${c.delivery_rate}%` : '—'}</td>
                    <td className={c.click_rate > 0 ? 'metric-amber' : ''}>{c.delivered > 0 ? `${c.click_rate}%` : '—'}</td>
                    <td className={c.bounce_rate > 5 ? 'metric-red' : ''}>{c.total_sent > 0 ? `${c.bounce_rate}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;
